/**
 * Address Page V2 View
 *
 * Pure render component - NO hooks, only JSX composition.
 * Receives data and handlers from Controller.
 * 
 * Uses WithSkeleton to prevent FOUC (Flash of Unstyled Content)
 * during hydration for each major section.
 */

import dynamic from 'next/dynamic';
import { ResponsiveContainer } from 'components/atomic/atoms/responsive-container';
import { HydrationGuard } from 'components/atomic/atoms/hydration-guard';

import { DesktopLayout } from './layouts/desktop-layout';
import { MobileLayout } from './layouts/mobile-layout';

import type { AddressPageV2ViewData, AddressPageV2ViewHandlers } from './address-page-v2-controller';

const ProviderBanner = dynamic(() =>
  import('components/banner').then(mod => mod.ProviderBanner)
);

export interface AddressPageV2ViewProps {
  data: AddressPageV2ViewData;
  handlers: AddressPageV2ViewHandlers;
}

/**
 * AddressPageV2View
 *
 * Pure render component. Receives all data and handlers from Controller.
 * Delegates to DesktopLayout or MobileLayout based on viewport.
 */
export function AddressPageV2View({ data, handlers }: AddressPageV2ViewProps) {
  const { topOfListingRef, commonData } = data;
  const { showTermsOfUseBanner } = commonData;
  const { onTermsOfUseBannerDismiss } = handlers;

  return (
    <HydrationGuard minHeight="100vh">
      <div ref={topOfListingRef} id="address-page-container">
        {showTermsOfUseBanner && <ProviderBanner onDismiss={onTermsOfUseBannerDismiss} />}

        <ResponsiveContainer show="tablet-large-up" renderMode="conditional">
          <DesktopLayout data={data} handlers={handlers} />
        </ResponsiveContainer>

        <ResponsiveContainer show="mobile" renderMode="conditional">
          <MobileLayout data={data} handlers={handlers} />
        </ResponsiveContainer>
      </div>
    </HydrationGuard>
  );
}

export default AddressPageV2View;
